/**
 * Where the selected agent sits when this service runs (task 796).
 *
 * Report open question 2 — for a flow that uses the same agent at more than one
 * node, should the rail carry one entry per node instead of one per agent, since
 * node-level overrides can differ? Current implementation: ONE rail entry per
 * agent, and this panel is where each node appearance is listed separately, with
 * its own flow strip and its own autonomy settings. If the multi-node case ever
 * becomes common enough that per-node overrides need their own selection, the
 * rail is where that changes; this panel already models the plurality.
 *
 * The strip is derived from the definition's top-level edges and is deliberately
 * one hop deep in each direction — the Config tab owns the whole graph. Branch
 * nodes that nest their own nodes/edges are shown as the single branch node they
 * are; their interior is not walked.
 *
 * The autonomy readout mirrors one non-obvious engine rule: `maxIterations` is
 * ignored unless at least one evaluation criterion (`confidenceThreshold`,
 * `qualityThreshold`, or `satisfactionCheck`) is configured — without one,
 * `executeWithAutonomy` forces a single iteration to avoid wasteful re-runs. A
 * node declaring `maxIterations: 5` and nothing else runs exactly once, so the
 * panel says so rather than printing a 5 that never happens.
 */
import type { AgentNode, CollaborationNode } from '@/types/definition';
import { neighborsOfNode, nodeLabel } from './agentFlow';

interface AgentFlowRolePanelProps {
  definition: Record<string, unknown> | null | undefined;
  /** Every node in this flow that runs the selected agent. */
  nodes: AgentNode[];
}

function FlowNode({ node, current = false }: { node: CollaborationNode; current?: boolean }) {
  return (
    <span
      className={`rounded-md border px-2.5 py-1 text-xs ${
        current
          ? 'border-indigo-400 bg-indigo-50 font-semibold text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-200'
          : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
      }`}
    >
      <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {node.type}
      </span>
      {nodeLabel(node)}
    </span>
  );
}

function Arrow() {
  return (
    <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">
      →
    </span>
  );
}

function NodeAppearance({
  definition,
  node,
}: {
  definition: Record<string, unknown> | null | undefined;
  node: AgentNode;
}) {
  const { upstream, downstream } = neighborsOfNode(definition, node.id);
  const autonomy = node.autonomy;

  const hasEvaluationCriteria =
    autonomy?.confidenceThreshold !== undefined ||
    autonomy?.qualityThreshold !== undefined ||
    autonomy?.satisfactionCheck === true;

  return (
    <div data-testid={`agent-flow-node-${node.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {upstream.length === 0 ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {node.entry ? 'Flow entry' : 'No upstream node'}
          </span>
        ) : (
          upstream.map((up) => <FlowNode key={`up-${up.id}`} node={up} />)
        )}
        <Arrow />
        <FlowNode node={node} current />
        <Arrow />
        {downstream.length === 0 ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">Flow ends here</span>
        ) : (
          downstream.map((down) => <FlowNode key={`down-${down.id}`} node={down} />)
        )}
      </div>

      <dl className="mt-3 grid grid-cols-[minmax(0,10rem)_1fr] gap-x-4 gap-y-1.5 text-xs">
        <dt className="text-gray-500 dark:text-gray-400">Max iterations</dt>
        <dd className="text-gray-900 dark:text-gray-100">
          {hasEvaluationCriteria ? (
            (autonomy?.maxIterations ?? 1)
          ) : (
            <>
              1
              <span className="ml-1 text-gray-500 dark:text-gray-400">
                {autonomy?.maxIterations
                  ? `(${autonomy.maxIterations} declared, but the agent runs once until a stopping criterion is set)`
                  : '(no stopping criterion configured)'}
              </span>
            </>
          )}
        </dd>

        <dt className="text-gray-500 dark:text-gray-400">Data requests</dt>
        <dd className="text-gray-900 dark:text-gray-100">
          {autonomy?.allowDataRequests ? 'Allowed' : 'Not permitted'}
        </dd>

        {autonomy?.confidenceThreshold !== undefined && (
          <>
            <dt className="text-gray-500 dark:text-gray-400">Confidence threshold</dt>
            <dd className="text-gray-900 dark:text-gray-100">{autonomy.confidenceThreshold}</dd>
          </>
        )}
        {autonomy?.qualityThreshold !== undefined && (
          <>
            <dt className="text-gray-500 dark:text-gray-400">Quality threshold</dt>
            <dd className="text-gray-900 dark:text-gray-100">{autonomy.qualityThreshold}</dd>
          </>
        )}
        {autonomy?.satisfactionCheck !== undefined && (
          <>
            <dt className="text-gray-500 dark:text-gray-400">Satisfaction check</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {autonomy.satisfactionCheck ? 'Enabled' : 'Disabled'}
            </dd>
          </>
        )}

        {node.llmConfigId && (
          <>
            <dt className="text-gray-500 dark:text-gray-400">LLM config override</dt>
            <dd className="font-mono text-gray-900 dark:text-gray-100">{node.llmConfigId}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

export function AgentFlowRolePanel({ definition, nodes }: AgentFlowRolePanelProps) {
  return (
    <section
      data-testid="agent-flow-role-panel"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Role in this flow</h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Where this agent sits when the service runs
      </p>

      <div className="mt-4 flex flex-col gap-5">
        {nodes.map((node) => (
          <div key={node.id}>
            {nodes.length > 1 && (
              <p className="mb-2 font-mono text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                {node.id}
              </p>
            )}
            <NodeAppearance definition={definition} node={node} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default AgentFlowRolePanel;
